import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Plus, X, Star, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface SkillTag {
  id: string;
  name: string;
  category: string;
  is_custom?: boolean;
}

interface ContributorSkill {
  id: string;
  proficiency: string | null;
  years_experience: number | null;
  skill_tag_id: string;
  skill_type?: string;
  skill_tags: SkillTag;
}

interface SkillSelectorProps {
  userId: string;
  skills: ContributorSkill[];
  onUpdate: () => void;
  skillType?: 'current' | 'learning';
}

const PROFICIENCY_LEVELS = [
  { value: "beginner", label: "Beginner", stars: 1 },
  { value: "intermediate", label: "Intermediate", stars: 2 },
  { value: "expert", label: "Expert", stars: 3 },
];

const CATEGORY_ORDER = ["technical", "design", "business", "creative", "ministry", "other"];

const normalizeEntryName = (input: string): string => {
  return input
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const SkillSelector: React.FC<SkillSelectorProps> = ({ 
  userId, 
  skills, 
  onUpdate, 
  skillType = 'current' 
}) => {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const queryClient = useQueryClient();

  const isLearning = skillType === 'learning';

  // Fetch all available skill tags
  const { data: allSkills } = useQuery({
    queryKey: ["skill-tags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("skill_tags")
        .select("*")
        .order("category")
        .order("name");
      
      if (error) throw error;
      return data as SkillTag[];
    },
  });

  // Add skill mutation
  const addSkillMutation = useMutation({
    mutationFn: async ({ skillTagId, proficiency }: { skillTagId: string; proficiency: string }) => {
      const { error } = await supabase
        .from("contributor_skills")
        .insert({
          profile_id: userId,
          skill_tag_id: skillTagId,
          proficiency: isLearning ? null : proficiency,
          skill_type: skillType,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      setSearchInput("");
      setOpen(false);
      onUpdate();
    },
    onError: (error) => {
      toast.error(`Failed to add skill: ${error.message}`);
    },
  });

  // Update proficiency mutation
  const updateProficiencyMutation = useMutation({
    mutationFn: async ({ skillId, proficiency }: { skillId: string; proficiency: string }) => {
      const { error } = await supabase
        .from("contributor_skills")
        .update({ proficiency })
        .eq("id", skillId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      onUpdate();
    },
    onError: (error) => {
      toast.error(`Failed to update proficiency: ${error.message}`);
    },
  });

  // Create custom skill mutation
  const createCustomSkillMutation = useMutation({
    mutationFn: async (skillName: string) => {
      const normalizedName = normalizeEntryName(skillName);
      
      // Check if skill already exists (case-insensitive)
      const { data: existing } = await supabase
        .from("skill_tags")
        .select("id")
        .ilike("name", normalizedName)
        .maybeSingle();
      
      if (existing) {
        // Add existing skill to user
        await supabase.from("contributor_skills").insert({
          profile_id: userId,
          skill_tag_id: existing.id,
          proficiency: isLearning ? null : "intermediate",
          skill_type: skillType,
        });
        return existing.id;
      }
      
      // Insert new custom skill
      const { data: newSkill, error } = await supabase
        .from("skill_tags")
        .insert({
          name: normalizedName,
          category: "other",
          is_custom: true,
        })
        .select("id")
        .single();
      
      if (error) throw error;
      
      // Add to user's skills
      await supabase.from("contributor_skills").insert({
        profile_id: userId,
        skill_tag_id: newSkill.id,
        proficiency: isLearning ? null : "intermediate",
        skill_type: skillType,
      });
      
      // Refresh skill tags
      queryClient.invalidateQueries({ queryKey: ["skill-tags"] });
      
      return newSkill.id;
    },
    onSuccess: () => {
      setSearchInput("");
      setOpen(false);
      onUpdate();
      toast.success(isLearning ? "Learning goal added!" : "Custom skill added!");
    },
    onError: (error) => {
      toast.error(`Failed to add skill: ${error.message}`);
    },
  });

  // Remove skill mutation
  const removeSkillMutation = useMutation({
    mutationFn: async (skillId: string) => {
      const { error } = await supabase
        .from("contributor_skills")
        .delete()
        .eq("id", skillId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      onUpdate();
    },
    onError: (error) => {
      toast.error(`Failed to remove skill: ${error.message}`);
    },
  });

  // Filter out already added skills (across both types)
  const availableSkills = allSkills?.filter(
    skill => !skills.some(s => s.skill_tag_id === skill.id)
  ) || [];

  // Group skills by category
  const groupedAvailableSkills = CATEGORY_ORDER.reduce((acc, category) => {
    const categorySkills = availableSkills.filter(s => s.category === category);
    if (categorySkills.length > 0) {
      acc[category] = categorySkills;
    }
    return acc;
  }, {} as Record<string, SkillTag[]>);

  // Check if search term exactly matches any available skill name
  const searchExactMatch = availableSkills.some(
    skill => skill.name.toLowerCase() === searchInput.trim().toLowerCase()
  );

  const renderProficiencyStars = (level: string) => {
    const levelConfig = PROFICIENCY_LEVELS.find(l => l.value === level);
    if (!levelConfig) return null;
    
    return (
      <span className="ml-1 inline-flex">
        {Array.from({ length: levelConfig.stars }).map((_, i) => (
          <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
        ))}
      </span>
    );
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      technical: "Technical",
      design: "Design",
      business: "Business",
      creative: "Creative",
      ministry: "Ministry",
      other: "Other",
    };
    return labels[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  const isAdding = addSkillMutation.isPending || createCustomSkillMutation.isPending;

  return (
    <div className="space-y-3">
      {/* Current skills with inline proficiency editing */}
      <div className="flex flex-wrap gap-2">
        {skills.map(skill => (
          isLearning ? (
            // Learning skills - simple badges without proficiency
            <Badge
              key={skill.id}
              variant="outline"
              className="bg-primary/5 border-primary/20 pr-1"
            >
              <span>{skill.skill_tags.name}</span>
              <button
                onClick={() => removeSkillMutation.mutate(skill.id)}
                className="ml-1 p-0.5 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ) : (
            // Current skills - with proficiency popover
            <Popover key={skill.id}>
              <PopoverTrigger asChild>
                <Badge
                  variant="secondary"
                  className="cursor-pointer hover:bg-secondary/80 transition-colors pr-1"
                >
                  <span>{skill.skill_tags.name}</span>
                  {skill.proficiency && renderProficiencyStars(skill.proficiency)}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSkillMutation.mutate(skill.id);
                    }}
                    className="ml-1 p-0.5 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </PopoverTrigger>
              <PopoverContent className="w-36 p-2" align="start">
                <p className="text-xs font-medium mb-2 text-muted-foreground">Proficiency</p>
                <div className="space-y-1">
                  {PROFICIENCY_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => updateProficiencyMutation.mutate({ 
                        skillId: skill.id, 
                        proficiency: level.value 
                      })}
                      className={cn(
                        "w-full text-left px-2 py-1.5 rounded text-sm flex items-center justify-between",
                        "hover:bg-accent transition-colors",
                        skill.proficiency === level.value && "bg-accent"
                      )}
                    >
                      <span>{level.label}</span>
                      <span className="inline-flex">
                        {Array.from({ length: level.stars }).map((_, i) => (
                          <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
                        ))}
                      </span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )
        ))}

        {/* Add skill button */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-6 gap-1">
              {isAdding ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
              {isLearning ? "Add Goal" : "Add Skill"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput 
                placeholder={isLearning ? "Search skills to learn..." : "Search skills..."} 
                value={searchInput}
                onValueChange={setSearchInput}
              />
              <CommandList>
                <CommandEmpty>
                  {searchInput.trim().length >= 2 && !searchExactMatch && (
                    <button
                      onClick={() => createCustomSkillMutation.mutate(searchInput)}
                      className="w-full px-2 py-3 text-sm text-left hover:bg-accent flex items-center gap-2"
                      disabled={createCustomSkillMutation.isPending}
                    >
                      <Plus className="h-4 w-4" />
                      Add "{normalizeEntryName(searchInput)}" as new skill
                    </button>
                  )}
                  {searchInput.trim().length < 2 && (
                    <p className="py-3 text-center text-sm text-muted-foreground">
                      {isLearning ? "Type to find skills you want to learn" : "Type to search or add custom skill"}
                    </p>
                  )}
                </CommandEmpty>
                {Object.entries(groupedAvailableSkills).map(([category, categorySkills]) => (
                  <CommandGroup key={category} heading={getCategoryLabel(category)}>
                    {categorySkills.map(skill => (
                      <CommandItem
                        key={skill.id}
                        value={skill.name}
                        onSelect={() => {
                          addSkillMutation.mutate({ 
                            skillTagId: skill.id, 
                            proficiency: "intermediate" 
                          });
                        }}
                        className="cursor-pointer"
                      >
                        {skill.name}
                        {skill.is_custom && (
                          <span className="ml-1 text-xs text-muted-foreground">(custom)</span>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {skills.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {isLearning 
            ? "No learning goals yet. Tell us what skills you'd like to develop."
            : "No skills added yet. Click \"Add Skill\" to get matched with relevant tasks."
          }
        </p>
      )}
    </div>
  );
};